/**
 * Shared Long Task Buffer — single PerformanceObserver for all consumers.
 *
 * Consolidates the three independent longtask observers that previously existed in:
 *  - main.ts (setupLongTaskTracking) — emits Long Task events with UI context
 *  - sessionMonitor.ts (setupLongTaskBuffer) — counts for session.degradation
 *  - networkDiagnostics.ts (createLongTaskObserver) — counts for likelyCause
 *
 * Now all consumers import from this single module to avoid 3× callback overhead.
 */

export interface LongTaskEntry {
  startTime: number;
  duration: number;
}

const MAX_ENTRIES = 2000;
const entries: LongTaskEntry[] = [];
let observer: PerformanceObserver | undefined;
let initialized = false;

//  Listeners that want to be notified of each new long task (e.g., main.ts for LogRocket emit)
type LongTaskListener = (entry: LongTaskEntry) => void;
const listeners: Set<LongTaskListener> = new Set();

/**
 * Initialize the shared long task observer. Safe to call multiple times — only the first call creates the observer.
 */
export function initLongTaskBuffer(): void {
  if (initialized) return;
  initialized = true;

  if (typeof PerformanceObserver === 'undefined') return;

  try {
    observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const item: LongTaskEntry = { startTime: entry.startTime, duration: entry.duration };

        entries.push(item);

        //  Evict oldest entries when cap is reached
        if (entries.length > MAX_ENTRIES) {
          entries.splice(0, entries.length - MAX_ENTRIES);
        }

        //  Notify listeners (for real-time emit to LogRocket)
        for (const listener of listeners) {
          try {
            listener(item);
          }
          catch {
            //  Don't let a bad listener break the observer
          }
        }
      }
    });

    observer.observe({ type: 'longtask', buffered: true });
  }
  catch {
    //  longtask observer not supported in this browser
  }
}

/**
 * Get the count of long task entries within a recent time window.
 */
export function getLongTaskCount(windowMs: number): number {
  const cutoff = performance.now() - windowMs;
  return entries.filter((e) => e.startTime >= cutoff).length;
}

/**
 * Get all buffered timestamps (read-only view).
 */
export function getLongTaskEntries(): readonly LongTaskEntry[] {
  return entries;
}

/**
 * Get total count of all buffered entries.
 */
export function getLongTaskTotal(): number {
  return entries.length;
}

/**
 * Register a listener that is called for each new long task entry.
 * Useful for real-time event emission (e.g., main.ts Long Task tracking).
 */
export function onLongTask(listener: LongTaskListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Disconnect the observer and clear all state. Primarily for testing.
 */
export function disconnect(): void {
  observer?.disconnect();
  observer = undefined;
  initialized = false;
  entries.length = 0;
  listeners.clear();
}
